import { TaskTypeEnum } from '@api';
import { STYLE_TASK_TYPES } from '@lib/styleMetric';
import { DataCollectionInputs } from '@types';
import * as yup from 'yup';

export type DataCollectionStepId = 'tabularMlStep1' | 'tabularMlStep2' | 'textGenerationStep' | 'styleStep';

type ValidationContext = {
  stepId: DataCollectionStepId;
  taskType?: string;
  selectedRiskMetrics?: Array<{ name: string }>;
};

const getTabularMlStep1Schema = () =>
  yup.object().shape({
    performanceMetricRanked: yup.array().of(yup.string()).min(1, 'Please select at least one option'),
  });

const getTabularMlStep2Schema = () =>
  yup.object().shape({
    protectedGroupsRanked: yup.array().of(yup.string()).min(1, 'Please select at least one option'),
    protectedGroupsFile: yup.mixed().required('Please upload predictions file'),
  });

const getTextGenerationStepSchema = ({ selectedRiskMetrics = [] }: ValidationContext) => {
  const hasPersonalDataExposure = selectedRiskMetrics.some(metric => metric.name === 'Personal Data Exposure');
  const hasInsightfulnessCriteria = selectedRiskMetrics.some(metric => metric.name === 'Criteria Insightfulness');
  const hasKeyTakeawayScore = selectedRiskMetrics.some(metric => metric.name === 'Key Takeaway Score');

  const shape: Record<string, yup.AnySchema> = {};

  if (hasPersonalDataExposure) {
    shape.textGenerationPersonalDataCategories = yup
      .array()
      .of(yup.number())
      .min(1, 'Please select at least one personal data exposure category.');
  }

  if (hasKeyTakeawayScore || hasInsightfulnessCriteria) {
    shape.reportType = yup.number().required('Please select a report type.');
  }

  if (hasInsightfulnessCriteria) {
    shape.textGenerationInsightfulnessCriteria = yup
      .array()
      .of(yup.number())
      .min(1, 'Please select at least one insightfulness criteria.');
  }

  return yup.object().shape(shape);
};

// The style step only exists when the Style metric is selected
const getStyleStepSchema = () =>
  yup.object().shape({
    styleCriteria: yup.array().of(yup.number()).min(1, 'Please select at least one style criterion.'),
  });

export const getDataCollectionStepSchema = (ctx: ValidationContext) => {
  // Style is shared by several task types, so it is resolved before the per-task-type branches
  if (ctx.stepId === 'styleStep' && STYLE_TASK_TYPES.includes(ctx.taskType as TaskTypeEnum)) {
    return getStyleStepSchema();
  }

  if (
    ctx.taskType === TaskTypeEnum.singleTurnChat ||
    ctx.taskType === TaskTypeEnum.informationExtraction ||
    ctx.taskType === TaskTypeEnum.conversationalAgent ||
    ctx.taskType === TaskTypeEnum.voiceAgent
  ) {
    return yup.object().shape({});
  }

  if (ctx.stepId === 'tabularMlStep1' && ctx.taskType === TaskTypeEnum.tabularMl) {
    return getTabularMlStep1Schema();
  }

  if (ctx.stepId === 'tabularMlStep2' && ctx.taskType === TaskTypeEnum.tabularMl) {
    return getTabularMlStep2Schema();
  }

  if (ctx.stepId === 'textGenerationStep' && ctx.taskType === TaskTypeEnum.textGeneration) {
    return getTextGenerationStepSchema(ctx);
  }

  return yup.object().shape({});
};

export const validateDataCollectionStep = async (data: Partial<DataCollectionInputs>, ctx: ValidationContext) => {
  const schema = getDataCollectionStepSchema(ctx);
  await schema.validate(data, { abortEarly: false });
};
